import { randomUUID } from 'base/uuid';
import { AtomDomain } from 'blocks/woot_woot/atom';
import { ConfigOf, MutableOf, StateOf } from 'blocks/woot_woot/base';
import { ListDomain } from 'blocks/woot_woot/list';
import { withMigration } from 'blocks/woot_woot/migration';
import { RecordDomain } from 'blocks/woot_woot/record';
import { RecordUnionDomain } from 'blocks/woot_woot/record_union';
import {
  createStringMapStateSchema,
  recordMap,
  StringMapDomain,
} from 'blocks/woot_woot/string_map';
import { z } from 'zod';

/**
 * The entire project data structure has a schema version however updating
 * that requires server downtime. To avoid having to do that for likely future
 * iterations on the style guide builder we have concepts have their own
 * version which we independently track. This just gives us a super easy way to
 * detect out of date concepts so we can migrate them over silently.
 */
export const CONCEPT_SCHEMA_VERSION = 6;

export enum UiStyle {
  Default = 'default',
  Bubble = 'bubble',
  Brick = 'brick',
  Gradient = 'gradient',
  Sleek = 'sleek',
  Elevate = 'elevate',
}

export enum UiStyleRadiusOption {
  Radius0 = 'radius-0',
  Radius1 = 'radius-1',
  Radius2 = 'radius-2',
  Radius3 = 'radius-3',
}

export enum CardStyle {
  Default = 'default',
  Outlined = 'outlined',
  Flat = 'flat',
  Edgy = 'edgy',
}

export enum CardStyleRadiusOption {
  Radius0 = 'radius-0',
  Radius1 = 'radius-1',
  Radius2 = 'radius-2',
  Radius3 = 'radius-3',
}

const DEFAULT_FONT_FAMILIES = {
  heading: {
    slug: 'G-Inter',
    locked: false,
    liked: [],
  },
  body: {
    slug: 'G-Inter',
    locked: false,
    liked: [],
  },
} as const;

const DEFAULT_TYPE_SETTINGS = {
  size: 'small',
  weight: 'regular',
  style: 'default',
} as const;

const fontFamilyDomain = new RecordDomain({
  slug: new AtomDomain(z.string()),
  locked: new AtomDomain(z.boolean()),
  liked: new ListDomain(new AtomDomain(z.string())),
});
const fontFamiliesDomain = new RecordDomain({
  heading: fontFamilyDomain,
  body: fontFamilyDomain,
});
const typeSettingsDomain = new RecordDomain({
  size: new AtomDomain(z.enum(['small', 'regular', 'large'])),
  weight: new AtomDomain(z.enum(['regular', 'medium', 'bold'])),
  style: new AtomDomain(z.enum(['default', 'spacious', 'uppercase'])),
});

export type TypographyTarget = Exclude<
  keyof StateOf<ConfigOf<typeof fontFamiliesDomain>>,
  'mutable'
>;

export type TypeSettings = ConfigOf<typeof typeSettingsDomain>;
export type TypeSettingsState = StateOf<TypeSettings>;

export const colorDomain = new RecordDomain({
  name: new AtomDomain(z.string()),
  hex: new AtomDomain(z.string()),
  locked: new AtomDomain(z.boolean()),
});

const imagePropertiesDomain = new RecordDomain({
  fit: new AtomDomain(
    z
      .union([
        z.literal('cover'),
        z.literal('contain'),
        z.literal('none'),
        z.literal('fill'),
      ])
      .optional(),
  ),
  position: new AtomDomain(z.string().optional()),
  ratio: new AtomDomain(z.string().optional()),
  crop: new AtomDomain(
    z.tuple([z.number(), z.number(), z.number(), z.number()]),
  ),
});

export const imageRefDomain = new RecordUnionDomain({
  private: {
    id: new AtomDomain(z.number()),
    properties: imagePropertiesDomain,
  },
  unsplash: {
    base: new AtomDomain(z.string()),
    // TODO: zain, is this real or requested.
    width: new AtomDomain(z.number()),
    height: new AtomDomain(z.number()),
    properties: imagePropertiesDomain,
  },
  placeholder: {
    id: new AtomDomain(z.string()),
  },
});

export type ImageRefState = StateOf<ConfigOf<typeof imageRefDomain>>;
export type ImageRef = MutableOf<ConfigOf<typeof imageRefDomain>>;

const designSchemeDomain = new RecordDomain({
  id: new AtomDomain(z.string().uuid()),
  shadeKey: new AtomDomain(z.string()),
});
export type DesignScheme = MutableOf<ConfigOf<typeof designSchemeDomain>>;
export type DesignSchemeState = StateOf<ConfigOf<typeof designSchemeDomain>>;

export const aDesignScheme = (
  part?: Partial<DesignSchemeState>,
): DesignSchemeState => ({
  id: randomUUID(),
  shadeKey: '',
  ...part,
});

const conceptDomainWithoutMigration = new RecordDomain({
  uuid: new AtomDomain(z.string()),
  images: new StringMapDomain(imageRefDomain),
  version: new AtomDomain(z.literal(CONCEPT_SCHEMA_VERSION)),
  updatedAt: new AtomDomain(z.string()),
  createdAt: new AtomDomain(z.string()),
  createdBy: new AtomDomain(z.number().default(-1)),
  links: new ListDomain(new AtomDomain(z.string())),
  galleryPosition: new AtomDomain(z.number().min(1).max(3).optional()),
  hidden: new AtomDomain(z.boolean().default(false)),
  description: new AtomDomain(z.string().optional().default('')),
  schemeOverrideByElementId: new StringMapDomain(new AtomDomain(z.string())),
  theme: new AtomDomain(z.enum(['light', 'dark'])),
  chromatics: new ListDomain(colorDomain),
  // Points to a color in the chromatics array.
  neutral: new RecordDomain({
    tintIndex: new AtomDomain(z.number().optional()),
    locked: new AtomDomain(z.boolean()),
  }),
  // Typography.
  fontFamilies: fontFamiliesDomain,
  typeSettings: typeSettingsDomain,
  fontOnboardingCompleted: new AtomDomain(z.boolean().optional().default(true)),
  // UI Elements
  uiStyle: new AtomDomain(
    z
      .nativeEnum(UiStyle)
      .default(UiStyle.Default)
      .or(z.literal('subtle').transform(() => UiStyle.Default)),
  ),
  uiStyleRadiusOption: new AtomDomain(
    z.nativeEnum(UiStyleRadiusOption).default(UiStyleRadiusOption.Radius0),
  ),
  uiStyleLocked: new AtomDomain(z.boolean().default(false)),
  cardStyle: new AtomDomain(z.nativeEnum(CardStyle).default(CardStyle.Default)),
  cardStyleRadiusOption: new AtomDomain(
    z.nativeEnum(CardStyleRadiusOption).default(CardStyleRadiusOption.Radius0),
  ),
  cardStyleLocked: new AtomDomain(z.boolean().default(false)),

  designSchemes: new ListDomain(designSchemeDomain),
  // Map of element UUID -> scheme UUID
  designSchemeIdByElementId: new StringMapDomain(
    new AtomDomain(z.string().uuid()),
  ),
});

/**
 * A project concept. Represents a generated style guide for a project that can
 * be used to style a wireframe.
 */
export type Concept = MutableOf<ConfigOf<typeof conceptDomainWithoutMigration>>;
/** Readonly snapshot of a Concept. */
export type ConceptState = StateOf<
  ConfigOf<typeof conceptDomainWithoutMigration>
>;

export const conceptDomain = withMigration(
  withMigration(
    conceptDomainWithoutMigration,
    z
      // Older versions only created during pre-launch testing. Don't bother
      // migrating if we encounter these, just reset their state.
      .object({
        version: z.union([
          z.literal(1),
          z.literal(2),
          z.literal(3),
          z.literal(4),
        ]),
      })
      .transform(() => aConcept()),
  ),
  z
    .object({
      uuid: z.string(),
      images: createStringMapStateSchema(imageRefDomain.stateSchema),
      version: z.literal(5),
      updatedAt: z.string(),
      createdAt: z.string(),
      createdBy: z.number(),
      links: z.array(z.string()),
      galleryPosition: z.number().min(1).max(3).optional(),
      hidden: z.boolean().default(false),
      description: z.string().optional().default(''),
      schemeOverrideByElementId: createStringMapStateSchema(z.string()),
      theme: z.enum(['light', 'dark']),
      chromatics: z.array(colorDomain.stateSchema),
      neutral: z.object({
        tintIndex: z.number().optional(),
        locked: z.boolean(),
      }),
      fontFamilies: fontFamiliesDomain.stateSchema,
      typeSettings: typeSettingsDomain.stateSchema,
      fontOnboardingCompleted: z.boolean().optional().default(true),
    })
    .transform((obj): ConceptState => {
      const newSchemeOverrideByElementId = new Map<string, string>(
        obj.schemeOverrideByElementId.entries(),
      );

      if (obj.theme === 'dark') {
        for (const [
          elementId,
          scheme,
        ] of obj.schemeOverrideByElementId.entries()) {
          const oldShade = scheme.match(/-(\d+)$/)?.[1];
          if (oldShade === undefined) continue;

          const newShade =
            (scheme.startsWith('neutral') ? 7 : 8) - parseInt(oldShade, 10);
          const newScheme = scheme.replace(/-\d+$/, `-${newShade}`);

          newSchemeOverrideByElementId.set(elementId, newScheme);
        }
      }

      return {
        ...obj,
        // Appease the type-checker re: optionals
        galleryPosition: obj.galleryPosition ?? undefined,
        neutral: {
          ...obj.neutral,
          tintIndex: obj.neutral.tintIndex ?? undefined,
        },
        // Actual migration
        schemeOverrideByElementId: recordMap(newSchemeOverrideByElementId),
        version: 6,
        // Misc fields introduced after the migration was written
        uiStyle: UiStyle.Default,
        uiStyleRadiusOption: UiStyleRadiusOption.Radius0,
        uiStyleLocked: false,
        cardStyle: CardStyle.Default,
        cardStyleRadiusOption: CardStyleRadiusOption.Radius0,
        cardStyleLocked: false,
        designSchemes: [],
        designSchemeIdByElementId: recordMap(),
      };
    }),
);

export function aPrivateImageRef(id: number): ImageRefState {
  return {
    type: 'private',
    id,
    properties: {
      fit: 'cover',
      position: undefined,
      ratio: undefined,
      crop: [0, 0, 0, 0],
    },
  };
}

export function aConcept(part?: Partial<ConceptState>): ConceptState {
  return {
    uuid: randomUUID(),
    images: recordMap(),
    version: CONCEPT_SCHEMA_VERSION,
    updatedAt: '',
    createdAt: '',
    createdBy: -1,
    links: [],
    galleryPosition: undefined,
    hidden: false,
    description: '',
    schemeOverrideByElementId: recordMap(),
    theme: 'light',
    chromatics: [],
    neutral: {
      tintIndex: 0,
      locked: false,
    },
    fontFamilies: DEFAULT_FONT_FAMILIES,
    typeSettings: DEFAULT_TYPE_SETTINGS,
    fontOnboardingCompleted: false,
    uiStyle: UiStyle.Default,
    uiStyleRadiusOption: UiStyleRadiusOption.Radius0,
    uiStyleLocked: false,
    cardStyle: CardStyle.Default,
    cardStyleRadiusOption: CardStyleRadiusOption.Radius0,
    cardStyleLocked: false,
    designSchemes: [],
    designSchemeIdByElementId: recordMap(),
    ...part,
  };
}

const DESIGN_SCHEMA_VERSION = 1;

export const designDomain = new RecordDomain({
  version: new AtomDomain(z.literal(DESIGN_SCHEMA_VERSION)),
  conceptId: new AtomDomain(z.string().uuid()),
});
export type Design = MutableOf<ConfigOf<typeof designDomain>>;
export type DesignState = StateOf<ConfigOf<typeof designDomain>>;

export const aDesign = (part?: Partial<DesignState>): DesignState => ({
  version: DESIGN_SCHEMA_VERSION,
  conceptId: '',
  ...part,
});

export const MAX_CONCEPTS = 8;
